import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, GlassCard, Input, StyledSelect } from '@jumpstone/ui-react';
import type {
  UsePaymentSourceAccountsResult,
  UsePayrollSettingsResult,
} from '../../data/use_payroll_settings';
import {
  buildDisbursementPatch,
  isEmptyPatch,
  isValidDisbursementFileFormat,
  MAX_DISBURSEMENT_FILE_FORMAT_LENGTH,
} from '../../data/wagepro_store_payroll_settings';
import { PayrollSettingsStateGate } from './payroll_settings_state_gate';
import { formatPaymentSourceAccountLabel } from './post_run_payment_source_picker';

/** Matches `settings_screen.tsx`'s `SELECT_TRIGGER_CLASSES` -- same trigger shell, same reason: `triggerClassName` replaces rather than merges with `StyledSelect`'s default classes. */
const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full max-w-sm cursor-pointer items-center justify-between gap-2';

/** Sentinel option value for "no default account selected" -- `StyledSelect<V extends string>` has no null value slot. */
const NO_ACCOUNT_VALUE = '';

/**
 * Data Centre's Bank tab (#22730) — the entity's default salary-disbursement
 * account and payment-file format, real and editable. Reads/writes through
 * `usePayrollSettings()` (`default_disbursement_account_id`/
 * `disbursement_file_format`); the account picker's own option list comes
 * from the SEPARATE `usePaymentSourceAccounts()` fetch
 * (`payroll.listPaymentSourceAccounts`, #22324 C5) — the same narrow
 * bank/cash-asset predicate the backend's own `assertDisbursementAccount`
 * re-validates server-side on save (`apps/api/src/trpc/routers/payroll.ts`),
 * so this picker can only ever offer an account the write would accept.
 */
export function DataCentreBankSection({
  payrollSettings,
  accounts,
}: {
  payrollSettings: UsePayrollSettingsResult;
  accounts: UsePaymentSourceAccountsResult;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [accountId, setAccountId] = useState(NO_ACCOUNT_VALUE);
  const [fileFormat, setFileFormat] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Seed the draft from the resolved settings -- once per successful load,
  // never re-synced on every render (would clobber an in-progress edit).
  useEffect(() => {
    if (payrollSettings.state.status !== 'ready') return;
    setAccountId(
      payrollSettings.state.settings.default_disbursement_account_id ??
        NO_ACCOUNT_VALUE,
    );
    setFileFormat(
      payrollSettings.state.settings.disbursement_file_format ?? '',
    );
  }, [payrollSettings.state]);

  if (payrollSettings.state.status !== 'ready') {
    return (
      <PayrollSettingsStateGate
        state={payrollSettings.state}
        retry={payrollSettings.retry}
        t={t}
      />
    );
  }

  // Captured as its own const (not re-read as `payrollSettings.state.settings`
  // inside the closures below) -- TypeScript's control-flow narrowing of the
  // `status !== 'ready'` guard above does not carry through a member
  // expression into a nested function body, only through a plain local
  // identifier.
  const seededSettings = payrollSettings.state.settings;
  const canWrite = payrollSettings.save !== undefined;
  const fileFormatValid = isValidDisbursementFileFormat(fileFormat);
  const accountOptions = [
    {
      value: NO_ACCOUNT_VALUE,
      label: t('data_centre.disbursement_account_placeholder'),
    },
    ...accounts.accounts.map((account) => ({
      value: account.id,
      label: formatPaymentSourceAccountLabel(account),
    })),
  ];

  async function handleSave(): Promise<void> {
    if (!payrollSettings.save || !fileFormatValid) return;
    const patch = buildDisbursementPatch(seededSettings, {
      accountId,
      fileFormat,
    });
    // Nothing actually changed -- skip the round trip entirely rather than
    // re-sending an unchanged `default_disbursement_account_id` (which the
    // backend re-validates as entity-scoped/non-archived/bank-cash on
    // EVERY save; resending it here would risk failing the whole save if
    // that account has since been archived, see buildDisbursementPatch's
    // doc comment).
    if (isEmptyPatch(patch)) {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
      return;
    }
    setIsSaving(true);
    setSaveFailed(false);
    const result = await payrollSettings.save(patch);
    setIsSaving(false);
    if (result.outcome === 'success') {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
    } else {
      setSaveFailed(true);
    }
  }

  function handleCancel(): void {
    setAccountId(
      payrollSettings.settings?.default_disbursement_account_id ??
        NO_ACCOUNT_VALUE,
    );
    setFileFormat(payrollSettings.settings?.disbursement_file_format ?? '');
    setSaveFailed(false);
  }

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="grid grid-cols-1 gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
          <label className="nv-label">
            {t('data_centre.disbursement_account_label')}
          </label>
          {accounts.state.status === 'error' ||
          accounts.state.status === 'entitlement_required' ||
          accounts.state.status === 'no_entity' ? (
            // Deliberately does NOT render the picker at all here (not even
            // disabled): the seeded `accountId` may still hold a real,
            // previously-saved value, and an enabled-looking but
            // empty-options select could be "re-selected" back to the blank
            // placeholder, which `buildDisbursementPatch` would then read as
            // an intentional change and clear a real config on save
            // (adversarial-review finding, #22730). Editing this field is
            // simply unavailable until the account list loads.
            <p className="nv-help is-error mt-0">
              {t('payroll_settings.error_title')}{' '}
              <Button variant="ghost" size="xs" onClick={accounts.retry}>
                {t('payroll_settings.retry')}
              </Button>
            </p>
          ) : accounts.state.status === 'ready' &&
            accounts.accounts.length === 0 ? (
            <p className="nv-help mt-0">
              {t('data_centre.no_payment_source_accounts')}
            </p>
          ) : (
            <StyledSelect
              value={accountId}
              onChange={setAccountId}
              options={accountOptions}
              ariaLabel={t('data_centre.disbursement_account_label')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
              disabled={!canWrite || accounts.state.status === 'loading'}
            />
          )}
        </div>

        <div className="border-t border-[color:var(--line)]" />

        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
          <label className="nv-label">{t('data_centre.file_format')}</label>
          <div className="max-w-sm">
            <Input
              type="text"
              value={fileFormat}
              disabled={!canWrite}
              error={!fileFormatValid}
              placeholder={t('data_centre.file_format_placeholder')}
              onChange={(e) => {
                setFileFormat(e.target.value);
              }}
            />
            {!fileFormatValid && (
              <p className="nv-help is-error">
                {t('data_centre.file_format_too_long', {
                  max: MAX_DISBURSEMENT_FILE_FORMAT_LENGTH,
                })}
              </p>
            )}
          </div>
        </div>
      </div>

      {canWrite && (
        <div className="flex items-center justify-end gap-3 mt-4">
          {saveFailed && (
            <p className="nv-help is-error mt-0">
              {t('payroll_settings.save_error')}
            </p>
          )}
          <Button variant="ghost" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            loading={isSaving}
            disabled={!fileFormatValid}
          >
            {saved ? t('settings.saved') : t('settings.save')}
          </Button>
        </div>
      )}
    </GlassCard>
  );
}
